import Loading from 'components/loading';
import { EventProvider, useEvent } from 'contexts/event';
import { useSession } from 'contexts/session';
import { VoterProvider, useVoter } from 'contexts/voter';
import React, {useEffect, useRef, useState} from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import moment from 'moment/moment';

export async function loader({params}) {
    return params;
}

function VoterBallot() {
    const loaderData = useLoaderData();

    return (
        <EventProvider eventId={loaderData.eventid}>
            <VoterProvider voterId={loaderData.voterid}>
                <Ballot />
            </VoterProvider>
        </EventProvider>
    )
}

function Ballot() {
    const { tenant } = useSession();
    const { eventLoading, event } = useEvent();
    const { voterLoading, voter, getEntries, entriesLoading, entries,
        submittingBallot, submitBallot, ballotStatus, ballotStatusMessage } = useVoter();
    const [fname, setFname] = useState('');
    const [lname, setLname] = useState('');
    const [email, setEmail] = useState('');
    const classSelectRef = useRef([]);
    const navigate = useNavigate();
    const [selections, setSelections] = useState({});
    const [manualEntries, setManualEntries] = useState({});

    // Load saved selections from localStorage
    useEffect(() => {
        if (!entriesLoading && entries.length > 0) {
            const savedSelections = localStorage.getItem('ballotSelections');
            if (savedSelections) {
                setSelections(JSON.parse(savedSelections));
            }
            const savedFname = localStorage.getItem('ballotFname');
            if (savedFname) setFname(savedFname);
            const savedLname = localStorage.getItem('ballotLname');
            if (savedLname) setLname(savedLname);
            const savedEmail = localStorage.getItem('ballotEmail');
            if (savedEmail) setEmail(savedEmail);
        }
    }, [entriesLoading]);

    // Save selections to localStorage whenever they change, but only if not empty
    useEffect(() => {
        // Prevent overwriting with empty object on initial load
        if (Object.keys(selections).length > 0) {
            localStorage.setItem('ballotSelections', JSON.stringify(selections));
        }
    }, [selections]);

    // Save fname, lname, email to localStorage when they change
    useEffect(() => {
        if (fname) localStorage.setItem('ballotFname', fname);
    }, [fname]);
    useEffect(() => {
        if (lname) localStorage.setItem('ballotLname', lname);
    }, [lname]);
    useEffect(() => {
        if (email) localStorage.setItem('ballotEmail', email);
    }, [email]);

    // Handler for select change
    const handleSelectChange = (selectKey, carClass, value) => {
        setSelections(prev => ({
            ...prev,
            [selectKey]: value
        }));
        if (value !== `${carClass}-other`) {
            setManualEntries(prev => {
                const updated = { ...prev };
                delete updated[selectKey];
                return updated;
            });
        }
    };

    // Handler for manual input change
    const handleManualInputChange = (selectKey, carClass, value) => {
        setManualEntries(prev => ({
            ...prev,
            [selectKey]: value
        }));
    };

    useEffect(() => {
        if (!eventLoading) {
            getEntries(event.id);
        }
    }, [eventLoading])

    const handleResetBallot = () => {
        setSelections({});
    }

    const handleSubmitBallot = () => {
        let votes = [];
        let invalidManual = false;

        Object.keys(selections).forEach(selectKey => {
            const value = selections[selectKey];
            const [carClass] = selectKey.split('_');
            if (value === `${carClass}-other`) {
                const manualValue = manualEntries[selectKey];
                if (
                    manualValue &&
                    manualValue.trim() !== '' &&
                    //manualValue.startsWith(String(carClass)) &&
                    /^\d+$/.test(manualValue)
                ) {
                    votes.push(manualValue.trim());
                } else if (manualValue) {
                    invalidManual = true;
                }
            } else if (value !== '0') {
                votes.push(value);
            }
        });

        if (invalidManual) {
            alert('Manual entries must start with the class number and contain only digits.');
            return;
        }

        const data = {
            tenantId: tenant.id,
            voterId: voter.uuid,
            fname, lname, email,
            votes,
        }
        submitBallot(data);
        localStorage.clear();
    }

    const handleGoToPortal = () => {
        navigate('/portal');
    }

    return (
        <>
            {(eventLoading || voterLoading) ? <Loading /> : 
                <div className='voter-ballot'>
                    <h1>{event.name}</h1>
                    {moment().isBefore(moment(event.votingStart)) ?
                        <>
                            <h2>Voting has not begun</h2>
                            <p>Voting starts at {moment(event.votingStart).format('dddd, MMMM Do YYYY, h:mm a')}</p>
                            <div className='button-container'>
                                <button onClick={handleGoToPortal}>Go to club member portal</button>
                            </div>
                        </>
                        : moment().isAfter(moment(event.votingEnd)) ?
                        <>
                            <h2>Voting has ended</h2>
                            <p>Voting ended at {moment(event.votingEnd).format('dddd, MMMM Do YYYY, h:mm a')}</p>
                            <div className='button-container'>
                                <button onClick={handleGoToPortal}>Go to club member portal</button>
                            </div>
                        </>
                        : voter.voted ? 
                        <>
                            <h2>This ballot has been used</h2>
                            <p>Ballots can only be used one time. </p>
                            <div className='button-container'>
                                <button onClick={handleGoToPortal}>Go to club member portal</button>
                            </div>
                        </>
                        : ballotStatus === 'accepted' ?
                        <>
                            <h2>Your ballot has been accepted</h2>
                            <p>Your ballot and your votes have been accpted and counted.</p>

                            <div className='button-container'>
                                <button onClick={handleGoToPortal}>Go to club member portal</button>
                            </div>
                        </>
                        : 
                        <>
                            <h2>Voter Ballot</h2>
                            <p>Instructions: This is a one time use ballot. Do not use your browsers back button before
                                or after submitting the ballot and do not reload this page. You will not be able to re-submit
                                this ballot after the ballot has been submitted and accepted.<br/>
                                <br/>
                                Choose {event.votesPerClass} entry from each class. You do not need to make a choice for every class.
                                When you are done making your choices, submit the ballot and wait for the confirmation that
                                it was received.
                            </p>
                            <div className='ballot-form'>
                                <label>First name</label>
                                <input type='text' value={fname} onChange={(e) => setFname(e.target.value)} maxLength={64} />
                                <label>Last name</label>
                                <input type='text' value={lname} onChange={(e) => setLname(e.target.value)} maxLength={64} />
                                <label>Email</label>
                                <input type='text' value={email} onChange={(e) => setEmail(e.target.value)} maxLength={64} />
                                {entriesLoading ? <Loading /> :
                                    entries.map((ntry, idx) => (
                                        <ClassVoteChoices
                                            key={ntry.carClass}
                                            carClass={ntry.carClass}
                                            classDescription={ntry.classDescription}
                                            carNumbers={ntry.carNumbers}
                                            votesPerClass={event.votesPerClass}
                                            selections={selections}
                                            manualEntries={manualEntries}
                                            onSelectChange={handleSelectChange}
                                            onManualInputChange={handleManualInputChange}
                                        />
                                    ))
                                }
                                <div
                                 className='button-container'>
                                    <button onClick={handleResetBallot}>Reset Ballot</button>
                                    <button onClick={handleSubmitBallot} disabled={submittingBallot}>Submit Ballot</button>
                                </div>
                                {ballotStatus === 'rejected' &&
                                    <div>Your ballot was rejected for the following reason: {ballotStatusMessage}</div>
                                }
                            </div>
                            <p>Voter Id: {voter.uuid}</p>
                        </>
                    }
                </div>
            }
        </>
    )
}

function ClassVoteChoices({
    carClass,
    classDescription,
    carNumbers,
    votesPerClass,
    selections,
    manualEntries,
    onSelectChange,
    onManualInputChange
}) {
    return (
        <>
            {[...Array(votesPerClass)].map((_, choiceIdx) => {
                const selectKey = `${carClass}_${choiceIdx}`;
                return (
                    <React.Fragment key={selectKey}>
                        <label>
                            {`Class ${carClass} - ${classDescription} - choice ${choiceIdx + 1}`}
                        </label>
                        <select
                            value={selections[selectKey] || '0'}
                            onChange={e => onSelectChange(selectKey, carClass, e.target.value)}
                        >
                            <option value='0'>- no selection -</option>
                            {carNumbers.map(cn => <option key={cn}>{cn}</option>)}
                            <option value={`${carClass}-other`}>- other, not listed -</option>
                        </select>
                        {selections[selectKey] === `${carClass}-other` && (
                            <input
                                type="text"
                                placeholder={`Enter car number for class ${carClass}`}
                                value={manualEntries[selectKey] || ''}
                                onChange={e => onManualInputChange(selectKey, carClass, e.target.value)}
                                pattern={`^${carClass}\\d*$`}
                                maxLength={8}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
}

export default VoterBallot;